import React, { useState, useEffect, useRef } from "react";
import {
  getRecommendedPlayers,
  getRejectedPlayers,
  getAgentDescriptions,
  getEvaluationHistory,
  overridePlayerDecision,
  getRecommendedPlayersWithOverrides,
  getRejectedPlayersWithOverrides,
  isDecisionOverridden,
} from "../utils/matchingEngine";
import {
  XCircle,
  CheckCircle,
  AlertTriangle,
  UserCheck,
  UserX,
  Upload,
  Download,
  Plus,
} from "lucide-react";
import apiClient from "../utils/apiClient";
import { Pagination } from "./design-system";
import { C } from './design-system/primitives/constants';
import wbb_nil from "../data/wbb_nil.json";
import TransferPortalTab from "./scoutingComponents/TransferPortalTab";
import { getSelectedSport } from "../utils/helpers";
import { useTeam } from "../lib/teamConfig";

const OverrideModal = ({
  isOpen,
  onClose,
  Roster,
  currentDecision,
  onOverride,
  overrideDecision,
  setOverrideDecision,
  overrideReason,
  setOverrideReason,
}) => {
  if (!isOpen || !Roster) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div
        className="rounded-[10px] p-6 w-full max-w-md"
        style={{ background: C.s1, color: C.text, border: `1px solid ${C.border}` }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 style={{ fontSize: 18, fontWeight: 200, color: C.text }}>
            Human-in-the-Loop Override
          </h3>
          <button style={{ color: C.t3 }} onClick={onClose}>
            <XCircle size={20} />
          </button>
        </div>

        <div className="mb-4">
          <p className="font-medium" style={{ color: C.t2 }}>
            {Roster.first_name} {Roster.last_name} (
            {Roster.position_abbreviation})
          </p>
          <p className="text-sm" style={{ color: C.t3 }}>
            {Roster.origin_college_name || "Unknown"} • {Roster.stars} Stars
          </p>
        </div>

        <div className="mb-6">
          <p className="text-sm mb-3" style={{ color: C.t2 }}>
            The AI's current recommendation for this Roster is:
          </p>
          <div
            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
            style={{
              background: currentDecision ? `${C.elite}1A` : `${C.dev}1A`,
              color: currentDecision ? C.elite : C.dev,
            }}
          >
            {currentDecision ? (
              <>
                <CheckCircle size={16} className="mr-1" />
                <span>Recommended for UNC</span>
              </>
            ) : (
              <>
                <XCircle size={16} className="mr-1" />
                <span>Not Recommended</span>
              </>
            )}
          </div>
        </div>

        <div className="mb-6">
          <p className="text-sm font-medium mb-2" style={{ color: C.t2 }}>
            Would you like to override this decision?
          </p>
          <div className="flex space-x-3">
            <button
              className="flex-1 py-2 px-4 rounded-md border flex items-center justify-center"
              style={overrideDecision === true
                ? { background: `${C.elite}1A`, borderColor: C.elite, color: C.elite }
                : { borderColor: C.border, color: C.t2 }
              }
              onClick={() => setOverrideDecision(true)}
            >
              <UserCheck size={18} className="mr-2" />
              Approve Roster
            </button>
            <button
              className="flex-1 py-2 px-4 rounded-md border flex items-center justify-center"
              style={overrideDecision === false
                ? { background: `${C.dev}1A`, borderColor: C.dev, color: C.dev }
                : { borderColor: C.border, color: C.t2 }
              }
              onClick={() => setOverrideDecision(false)}
            >
              <UserX size={18} className="mr-2" />
              Reject Roster
            </button>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium mb-1" style={{ color: C.t2 }}>
            Reason for Override (optional)
          </label>
          <textarea
            className="w-full rounded-md p-2 text-sm"
            style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.text }}
            rows="3"
            placeholder="Explain why you're overriding the AI's decision..."
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
          />
        </div>

        <div className="flex justify-end space-x-3">
          <button
            className="px-4 py-2 rounded-md"
            style={{ border: `1px solid ${C.border}`, color: C.t2 }}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 rounded-md"
            style={{ background: C.gold, color: '#000' }}
            onClick={onOverride}
          >
            Confirm Override
          </button>
        </div>
      </div>
    </div>
  );
};

const LoadingState = () => (
  <div className="flex flex-col items-center justify-center py-12">
    <div className="w-16 h-16 mb-4 rounded-full animate-spin" style={{ borderTop: `4px solid ${C.gold}`, borderBottom: `4px solid ${C.gold}`, borderLeft: '4px solid transparent', borderRight: '4px solid transparent' }}></div>
    <p className="text-lg" style={{ color: C.t2 }}>
      Running multi-agent evaluation system...
    </p>
    <p className="text-sm mt-2" style={{ color: C.t3 }}>
      Analyzing Roster data and generating recommendations
    </p>
  </div>
);

// EvaluationHistory component (now just shows Transfer Portal)
const EvaluationHistory = (props) => {
  const {
    transferPortalPlayers,
    transferPortalLoading,
    selectedSchema,
    onSchemaChange,
    savedEvaluations,
    setSavedEvaluations,
  } = props;
  return (
    <div>
      <TransferPortalTab
        transferPortalPlayers={transferPortalPlayers}
        transferPortalLoading={transferPortalLoading}
        selectedSchema={selectedSchema}
        onSchemaChange={onSchemaChange}
        savedEvaluations={savedEvaluations}
        setSavedEvaluations={setSavedEvaluations}
        isDarkMode={true}
        {...props}
      />
    </div>
  );
};

// add new player modal:
const PlayerModal = ({
  isOpenPlayerModal,
  setIsOpenPlayerModal,
  modalType,
  players = [], // Default empty array
}) => {
  const [formData, setFormData] = useState({
    name: "",
    position: "",
    class: "",
    team: "",
    new_team: "",
    height: "",
    weight: "",
    dateEntered: (() => {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    })(),
    nillValue: "unlocked",
  });

  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [csvData, setCsvData] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Expected CSV columns
  const expectedColumns = [
    "Name",
    "Position",
    "Class",
    "Team",
    "New_Team",
    "Height",
    "Weight",
    "Date_Entered",
    "Nil_Value",
  ];

  // Handle player selection from dropdown
  const handlePlayerSelect = (player) => {

    setSearchQuery(player.name);
    setFormData({
      name: player?.name || "",
      position: player?.position || "",
      class: player?.class || "",
      team: player?.team || "",
      new_team: player?.new_team || "",
      height: player?.height || "",
      weight: player?.weight || "",
      dateEntered: formData.dateEntered,
      nillValue: player?.nillValue || "unlocked",
    });
    setIsDropdownOpen(false);
    setErrors({});
  };

  // Handle search input focus
  const handleSearchFocus = () => {
    setIsSearchFocused(true);
    setIsDropdownOpen(true);

  };

  // Handle search input blur
  const handleSearchBlur = (e) => {
    // Delay to allow dropdown clicks to register
    setTimeout(() => {
      setIsSearchFocused(false);
      if (
        !searchInputRef.current?.contains(document.activeElement) &&
        !dropdownRef.current?.contains(document.activeElement)
      ) {
        setIsDropdownOpen(false);
      }
    }, 150);
  };

  // Filter players based on search query
  const query = searchQuery.toLowerCase();

  const filteredPlayers = searchQuery === ""
    ? players
    : players.filter((player) =>
      player.name?.toLowerCase().includes(query) ||
      player.team?.toLowerCase().includes(query));


  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // If it's the name field, update search query and show dropdown
    if (name === "name") {
      setSearchQuery(value);
      if (value && isSearchFocused) {
        setIsDropdownOpen(true);
      } else if (!value) {
        setIsDropdownOpen(false);
      }
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    setErrors({});

    // Check if file is CSV
    if (file.type !== "text/csv" && !file.name.toLowerCase().endsWith(".csv")) {
      setErrors({ file: "Please upload a CSV file only" });
      return;
    }

    // Check file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setErrors({ file: "File size must be less than 5MB" });
      return;
    }

    // Read and validate CSV content
    const reader = new FileReader();
    reader.onload = (e) => {
      const csvText = e.target.result;
      const validation = validateCSVColumns(csvText);

      if (!validation.isValid) {
        setErrors({ file: validation.error });
        setUploadedFile(null);
        setCsvData(null);
        return;
      }

      // If validation passes, store the file and parsed data
      setUploadedFile(file);
      setCsvData(validation);
      setErrors({});
    };

    reader.onerror = () => {
      setErrors({ file: "Error reading file" });
    };

    reader.readAsText(file);
  };

  const removeFile = () => {
    setUploadedFile(null);
    setCsvData(null);
    setErrors({});
  };

  // Helper function to parse CSV row, handling quoted values
  const parseCSVRow = (row) => {
    const result = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < row.length; i++) {
      const char = row[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        result.push(current.trim().replace(/^"|"$/g, "")); // Remove surrounding quotes
        current = "";
      } else {
        current += char;
      }
    }

    // Add the last field
    result.push(current.trim().replace(/^"|"$/g, ""));

    return result;
  };

  const validateCSVColumns = (csvText) => {
    const lines = csvText.split("\n").filter((line) => line.trim()); // Remove empty lines
    if (lines.length === 0) {
      return { isValid: false, error: "CSV file is empty" };
    }

    const headers = parseCSVRow(lines[0]);

    // Check if all expected columns are present
    const missingColumns = expectedColumns.filter(
      (col) => !headers.includes(col)
    );
    if (missingColumns.length > 0) {
      return {
        isValid: false,
        error: `Missing required columns: ${missingColumns.join(", ")}`,
      };
    }

    // Check for extra columns (optional warning)
    const extraColumns = headers.filter(
      (header) => !expectedColumns.includes(header)
    );
    if (extraColumns.length > 0) {
      console.warn("Extra columns found (will be ignored):", extraColumns);
    }

    // Parse data rows into objects
    const data = lines.slice(1).map((line) => {
      const values = parseCSVRow(line);
      const rowObject = {};
      headers.forEach((header, index) => {
        rowObject[header] = values[index] || "";
      });
      return rowObject;
    });

    return { isValid: true, headers, data };
  };

  const handleUpload = async () => {
    if (uploadedFile && csvData) {
      try {
        // Now csvData.data contains objects with named properties
        const players = csvData.data.map((row) => ({
          name: row["Name"] || "",
          position: row["Position"] || "",
          class: row["Class"] || "",
          team: row["Team"] || "",
          new_team: row["New_Team"] || "",
          height: row["Height"] || "",
          weight: row["Weight"] || "",
          date_entered: row["Date_Entered"] || "",
          nil_value: row["Nil_Value"] || "",
        }));

        const payload = { players };

        const response = await fetch(
          "https://slam-all-python-359065791766.us-central1.run.app/MBB/tp-players/bulk_add_tp_players",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }
        );

        if (response.ok) {
          const result = await response.json();
          console.log("Upload successful:", result);
          alert(
            `File uploaded successfully! ${players.length} player records processed.`
          );
          handleClose();
        } else {
          throw new Error(`HTTP ${response.status}: ${await response.text()}`);
        }
      } catch (error) {
        console.error("Upload error:", error);
        alert(`Upload failed: ${error.message}`);
        handleClose(); // You might want to remove this if you want the modal to stay open on error
      }
    }
  };

  const downloadTemplate = () => {
    const csvContent =
      "Name,Position,Class,Team,New_Team,Height, Weight,Date_Entered,Nil_Value\nJohn Doe,Forward,A,Team none,Team Alpha,5.9,65,2024-01-15,unlocked\nJane Smith,Midfielder,B,Team none,Team Gamma,Team Delta,5.9,65,2024-01-20,locked";

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "player_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      "name",
      "position",
      "class",
      "new_team",
      "dateEntered",
      "height",
      "weight",
    ];

    requiredFields.forEach((field) => {
      if (!formData[field].trim()) {
        newErrors[field] = "This field is required";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (modalType === "inputForm") {
      if (validateForm()) {
        try {
          // API call to add player
          const response = await fetch(
            "https://slam-all-python-359065791766.us-central1.run.app/MBB/tp-players/add_tp_player",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(formData),
            }
          );

          if (response.ok) {
            const result = await response.json();
            console.log("Player added successfully:", result);
            alert("Player added successfully!");
            handleClose();
          } else {
            // Handle HTTP error responses
            const errorData = await response
              .json()
              .catch(() => ({ message: "Unknown error" }));
            console.error("Error adding player:", errorData);
            alert(
              `Error adding player: ${errorData.message || "Please try again"}`
            );
          }
        } catch (error) {
          // Handle network errors or other exceptions
          console.error("Network error:", error);
          alert("Network error. Please check your connection and try again.");
        }
      }
    } else {
      handleUpload();
    }
  };

  const handleClose = () => {
    setSearchQuery("");
    setIsDropdownOpen(false);
    setErrors({});
    setUploadedFile(null);
    setCsvData(null);
    setIsOpenPlayerModal(false);
    setErrors({});
    setUploadedFile(null);
    setCsvData(null);
    setDragActive(false);
    setIsDropdownOpen(false);
    // Reset form data
    setFormData({
      name: "",
      position: "",
      class: "",
      team: "",
      new_team: "",
      height: "",
      weight: "",
      dateEntered: (() => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      })(),
      nillValue: "unlocked",
    });
  };

  return (
    <>
      {/* Modal Overlay */}
      {isOpenPlayerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="relative rounded-[10px] max-w-2xl w-full max-h-[92vh] overflow-y-auto" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
            {/* Modal Header */}
            <div className="flex items-center justify-center p-4" style={{ borderBottom: `1px solid ${C.border}` }}>
              <h2 style={{ fontSize: 20, fontWeight: 200, color: C.text }}>
                {modalType === "inputForm"
                  ? "Add New Recruit"
                  : "Upload Players CSV"}
              </h2>
              <button
                onClick={handleClose}
                className="absolute p-1 transition-all duration-200 rounded-full right-3 top-3"
                style={{ color: C.t3 }}
              >
                <svg
                  className="w-7 h-7"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            {modalType === "inputForm" ? (
              <div className="px-6 py-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {/* Player Name with Search Dropdown */}
                  <div className="relative col-span-2">
                    <label
                      htmlFor="playerName"
                      className="block mb-1 text-sm font-medium transition-colors duration-200 cursor-pointer"
                      style={{ color: C.t2 }}
                    >
                      Player Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        ref={searchInputRef}
                        type="text"
                        id="playerName"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        onFocus={handleSearchFocus}
                        onBlur={handleSearchBlur}
                        style={{ background: C.s2, color: C.text }}
                      className={`w-full text-sm 2xl:text-base px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-[#E8B800] focus:border-[#E8B800] hover:border-[#E8B800] transition-all duration-200 ${errors.name
                          ? "border-red-500 hover:border-red-400"
                          : "border-[#1A1A1A]"
                          }`}
                        placeholder="Start typing to search existing players or enter new name"
                        autoComplete="off"
                      />

                      {/* Search Dropdown */}
                      {isDropdownOpen && filteredPlayers.length > 0 && (
                        <div
                          ref={dropdownRef}
                          className="absolute z-20 w-full mt-1 overflow-y-auto rounded-md max-h-60"
                          style={{ background: C.s2, border: `1px solid ${C.border}` }}
                        >
                          <div className="py-1">
                            {filteredPlayers.map((player, index) => (
                              <button
                                key={index}
                                type="button"
                                onClick={() => handlePlayerSelect(player)}
                                className="w-full px-3 py-2 text-sm text-left transition-colors duration-200 last:border-b-0 focus:outline-none"
                                style={{ color: C.text, borderBottom: `1px solid ${C.border}` }}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    {player.name}
                                  </span>
                                  <span className="text-[10px]" style={{ color: C.t3 }}>
                                    {player.team}
                                  </span>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Search Icon */}
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg
                          className="w-4 h-4"
                          style={{ color: C.t3 }}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </div>
                    </div>
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                    {players.length > 0 && (
                      <p className="mt-1 text-xs" style={{ color: C.t3 }}>
                        Start typing to search from {players.length} existing
                        players
                      </p>
                    )}
                  </div>

                  {/* Position */}
                  <div>
                    <label
                      htmlFor="position"
                      className="block mb-1 text-sm font-medium transition-colors duration-200 cursor-pointer"
                      style={{ color: C.t2 }}
                    >
                      Position <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="position"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      style={{ background: C.s2, color: C.text }}
                      className={`w-full text-sm 2xl:text-base px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-[#E8B800] focus:border-[#E8B800] hover:border-[#E8B800] transition-all duration-200 ${errors.position
                        ? "border-red-500 hover:border-red-400"
                        : "border-[#1A1A1A]"
                        }`}
                      placeholder="e.g., Forward, Midfielder, Defender"
                    />
                    {errors.position && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.position}
                      </p>
                    )}
                  </div>

                  {/* Class */}
                  <div>
                    <label
                      htmlFor="class"
                      className="block mb-1 text-sm font-medium transition-colors duration-200 cursor-pointer"
                      style={{ color: C.t2 }}
                    >
                      Class <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="class"
                      name="class"
                      value={formData.class}
                      onChange={handleInputChange}
                      style={{ background: C.s2, color: C.text }}
                      className={`w-full text-sm 2xl:text-base px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-[#E8B800] focus:border-[#E8B800] hover:border-[#E8B800] transition-all duration-200 ${errors.class
                        ? "border-red-500 hover:border-red-400"
                        : "border-[#1A1A1A]"
                        }`}
                      placeholder="Enter player class"
                    />
                    {errors.class && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.class}
                      </p>
                    )}
                  </div>

                  {/* Current Team */}
                  <div>
                    <label
                      htmlFor="currentTeam"
                      className="block mb-1 text-sm font-medium transition-colors duration-200 cursor-pointer"
                      style={{ color: C.t2 }}
                    >
                      Current Team
                    </label>
                    <input
                      type="text"
                      id="currentTeam"
                      name="team"
                      value={formData.team}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm transition-all duration-200 border rounded-md 2xl:text-base focus:outline-none focus:ring-1 focus:ring-[#E8B800] focus:border-[#E8B800] hover:border-[#E8B800]"
                      style={{ background: C.s2, color: C.text, borderColor: C.border }}
                      placeholder="Enter current team name"
                    />
                    {errors.team && (
                      <p className="mt-1 text-sm text-red-600">{errors.team}</p>
                    )}
                  </div>

                  {/* New Team */}
                  <div>
                    <label
                      htmlFor="newTeam"
                      className="block mb-1 text-sm font-medium transition-colors duration-200 cursor-pointer"
                      style={{ color: C.t2 }}
                    >
                      New Team <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="newTeam"
                      name="new_team"
                      value={formData.new_team}
                      onChange={handleInputChange}
                      style={{ background: C.s2, color: C.text }}
                      className={`w-full text-sm 2xl:text-base px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-[#E8B800] focus:border-[#E8B800] hover:border-[#E8B800] transition-all duration-200 ${errors.new_team
                        ? "border-red-500 hover:border-red-400"
                        : "border-[#1A1A1A]"
                        }`}
                      placeholder="Enter new team name"
                    />
                    {errors.new_team && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.new_team}
                      </p>
                    )}
                  </div>

                  {/* Height */}
                  <div>
                    <label
                      htmlFor="height"
                      className="block mb-1 text-sm font-medium transition-colors duration-200 cursor-pointer"
                      style={{ color: C.t2 }}
                    >
                      Height
                    </label>
                    <input
                      type="text"
                      id="height"
                      name="height"
                      value={formData.height}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm transition-all duration-200 border rounded-md 2xl:text-base focus:outline-none focus:ring-1 focus:ring-[#E8B800] focus:border-[#E8B800] hover:border-[#E8B800]"
                      style={{ background: C.s2, color: C.text, borderColor: C.border }}
                      placeholder="Enter height"
                    />
                    {errors.height && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.height}
                      </p>
                    )}
                  </div>

                  {/* Weight */}
                  <div>
                    <label
                      htmlFor="weight"
                      className="block mb-1 text-sm font-medium transition-colors duration-200 cursor-pointer"
                      style={{ color: C.t2 }}
                    >
                      Weight
                    </label>
                    <input
                      type="text"
                      id="weight"
                      name="weight"
                      value={formData.weight}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm transition-all duration-200 border rounded-md 2xl:text-base focus:outline-none focus:ring-1 focus:ring-[#E8B800] focus:border-[#E8B800] hover:border-[#E8B800]"
                      style={{ background: C.s2, color: C.text, borderColor: C.border }}
                      placeholder="Enter weight"
                    />
                    {errors.weight && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.weight}
                      </p>
                    )}
                  </div>

                  {/* Date Entered */}
                  <div>
                    <label
                      htmlFor="dateEntered"
                      className="block mb-1 text-sm font-medium transition-colors duration-200 cursor-pointer"
                      style={{ color: C.t2 }}
                    >
                      Date Entered <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="dateEntered"
                      name="dateEntered"
                      value={formData.dateEntered}
                      onChange={handleInputChange}
                      style={{ background: C.s2, color: C.text }}
                      className={`w-full text-sm 2xl:text-base px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-[#E8B800] focus:border-[#E8B800] hover:border-[#E8B800] transition-all duration-200 ${errors.dateEntered
                        ? "border-red-500 hover:border-red-400"
                        : "border-[#1A1A1A]"
                        }`}
                    />
                    {errors.dateEntered && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.dateEntered}
                      </p>
                    )}
                  </div>

                  {/* Status Toggle */}
                  <div>
                    <label className="block mb-1 text-sm font-medium transition-colors duration-200 cursor-pointer"
                      style={{ color: C.t2 }}>
                      Status
                    </label>
                    <div className="flex items-center mt-4 space-x-3">
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            nillValue:
                              prev.nillValue === "locked"
                                ? "unlocked"
                                : "locked",
                          }))
                        }
                        className="relative inline-flex h-5 w-11 items-center rounded-full transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-[#E8B800]"
                        style={{ background: formData.nillValue === "locked" ? C.gold : C.t3 }}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-[#F5F5F5] transition-transform duration-200 ${formData.nillValue === "locked"
                            ? "translate-x-1"
                            : "translate-x-6"
                            }`}
                        />
                      </button>
                      <span className="text-sm font-medium" style={{ color: C.text }}>
                        {formData.nillValue === "locked"
                          ? "Locked"
                          : "Unlocked"}
                      </span>
                      {formData.nillValue === "locked" ? (
                        <svg
                          className="w-4 h-4 transition-colors duration-200"
                          style={{ color: C.t2 }}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-4 h-4 transition-colors duration-200"
                          style={{ color: C.gold }}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // CSV Upload Mode
              <div className="px-6 py-4 space-y-4">
                {/* Download Template */}
                <div className="mb-4">
                  <button
                    onClick={downloadTemplate}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium transition-all duration-200 rounded-md"
                    style={{ color: C.gold }}
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Download CSV Template
                  </button>
                  <p className="mt-1 text-xs" style={{ color: C.t3 }}>
                    Download a sample CSV file to see the required format
                  </p>
                </div>

                {/* Required Columns Info */}
                <div className="p-3 mb-4 text-xs transition-colors duration-200 rounded-md" style={{ color: C.gold, background: C.g15 }}>
                  <p className="mb-1 font-medium">Required CSV Columns:</p>
                  <p>{expectedColumns.join(", ")}</p>
                </div>

                {/* Drag & Drop Area */}
                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  className="relative border-2 border-dashed rounded-[10px] py-2 px-6 text-center transition-all duration-200"
                  style={{
                    borderColor: dragActive || uploadedFile ? C.gold : C.border,
                    background: dragActive ? C.g15 : uploadedFile ? C.g08 : C.s2,
                  }}
                >
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />

                  {uploadedFile ? (
                    <div className="space-y-2">
                      <svg
                        className="w-10 h-10 mx-auto text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <div>
                        <p className="text-sm font-medium 2xl:text-base" style={{ color: C.text }}>
                          {uploadedFile.name}
                        </p>
                        <p className="text-sm" style={{ color: C.t3 }}>
                          {(uploadedFile.size / 1024).toFixed(1)} KB
                        </p>
                        {csvData && (
                          <p className="text-sm text-green-600">
                            {csvData.data.length} player records found
                          </p>
                        )}
                      </div>
                      <button
                        onClick={removeFile}
                        className="z-20 inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 transition-all duration-200 rounded hover:text-red-800 hover:bg-red-50"
                      >
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <svg
                        className="w-12 h-12 mx-auto"
                        style={{ color: C.t3 }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      <div>
                        <p className="text-sm font-medium 2xl:text-base" style={{ color: C.text }}>
                          {dragActive
                            ? "Drop your CSV file here"
                            : "Drag and drop your CSV file here"}
                        </p>
                        <p className="text-sm" style={{ color: C.t3 }}>
                          or click to browse
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Error Message */}
                {errors.file && (
                  <div className="flex items-center p-3 space-x-2 text-red-400 transition-colors duration-200 rounded-md bg-red-900/30">
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-sm">{errors.file}</span>
                  </div>
                )}

                {/* File Requirements */}
                <div className="p-3 text-sm transition-colors duration-200 rounded-md" style={{ color: C.t3, background: C.s2 }}>
                  <p className="mb-1 font-medium">File Requirements:</p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Only CSV files are accepted</li>
                    <li>Maximum file size: 5MB</li>
                    <li>Must contain all required columns in exact format</li>
                    <li>Use the template format for best results</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Modal Footer */}
            <div className="flex justify-between p-6 space-x-3" style={{ borderTop: `1px solid ${C.border}` }}>
              <button
                type="button"
                onClick={handleClose}
                className="w-full px-4 py-2 text-sm font-medium transition-all duration-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#E8B800]"
                style={{ color: C.t2, background: C.s2, border: `1px solid ${C.border}` }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={modalType !== "inputForm" && !uploadedFile}
                className="px-4 py-2 w-full text-sm font-medium border border-transparent rounded-md transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-[#E8B800] focus:ring-offset-2 focus:ring-offset-black"
                style={modalType === "inputForm" || uploadedFile
                  ? { background: C.gold, color: '#000' }
                  : { background: C.t4, color: C.t3, cursor: 'not-allowed' }
                }
              >
                {modalType === "inputForm" ? "Add Player" : "Upload CSV"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Main component
const MatchingEngine = () => {
  // Get sport selection from sidebar toggle
  const { selectedSport } = useTeam();

  const [isOpenPlayerModal, setIsOpenPlayerModal] = useState(false);
  const [modalType, setModalType] = useState("inputForm");

  // State variables
  const [matchedPlayers, setMatchedPlayers] = useState([]);
  const [rejectedPlayers, setRejectedPlayers] = useState([]);
  const [agentDescriptions, setAgentDescriptions] = useState([]);
  const [activeTab, setActiveTab] = useState("matches");
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [evaluationHistory, setEvaluationHistory] = useState([]);
  // We only have the Transfer Portal Data view now
  const [view, setView] = useState("history");
  const [transferPortalPlayers, setTransferPortalPlayers] = useState([]);
  const [transferPortalLoading, setTransferPortalLoading] = useState(false);
  // Initialize from sessionStorage using helper function
  // Map sidebar sport code → transfer portal schema
  // Basketball sports → MBB (or WBB), Baseball sports → CBB
  const BASKETBALL_SPORT_CODES = ['MBB', 'WBB', 'NBA', 'WNBA', 'EURO', 'GL', 'basketball'];
  const BASEBALL_SPORT_CODES = ['MLB', 'CBB'];
  const sportToPortalSchema = (sport) => {
    if (sport === 'WBB') return 'WBB';
    if (BASKETBALL_SPORT_CODES.includes(sport)) return 'MBB';
    if (BASEBALL_SPORT_CODES.includes(sport)) return 'CBB';
    return 'MBB'; // default to basketball
  };

  const [selectedSchema, setSelectedSchema] = useState(() => {
    return sportToPortalSchema(getSelectedSport());
  });
  const [savedEvaluations, setSavedEvaluations] = useState({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPlayers, setTotalPlayers] = useState(0);

  // Human-in-the-loop state
  const [humanInLoop, setHumanInLoop] = useState(true); // Enable human-in-the-loop by default
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [overrideDecision, setOverrideDecision] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideFeedback, setOverrideFeedback] = useState({
    show: false,
    message: "",
    isError: false,
  });

  const [sortField, setSortField] = useState("SLAM_SCORE");
  const [sortDirection, setSortDirection] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    position: [],
    class: [],
    latestTeam: [],
    name: "",
    destination: [],
    min_possessions: null,
    slamScore: [0, 100], // Default SLAM score range [min, max]
  });

  // players from evan miya
  const [dropdownPlayers, setDropdownPlayers] = useState([]);

  const fetchPlayers = async () => {
    try {
      const response = await fetch(
        "https://slam-all-python-359065791766.us-central1.run.app/MBB/players-stats-mia/not-in-tp"
        // "https://slam-all-python-359065791766.us-central1.run.app/MBB/players-stats-mia"
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Extract only player, team, and class from each player object
      const playersData = data.data.map((player) => ({
        name: player.player,
        team: player.team,
        class: player.class,
      }));

      setDropdownPlayers(playersData);
    } catch (error) {
      console.error("Error fetching players:", error);
      // Optional: Set an empty array or show error state
      setDropdownPlayers([]);
    }
  };

  useEffect(() => {
    fetchPlayers();
  }, []);

  // Sync with sessionStorage sport selection (from Settings)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'selectedSport' || e.key === null) {
        setSelectedSchema(sportToPortalSchema(getSelectedSport()));
      }
    };

    // Handle custom settings change event for immediate updates
    const handleSettingsChange = (e) => {
      const sportCode = e.detail?.sport?.code;
      if (sportCode) {
        setSelectedSchema(sportToPortalSchema(sportCode));
      }
    };

    // Listen for storage events (from Settings changes)
    window.addEventListener('storage', handleStorageChange);
    // Listen for custom settings change event (immediate updates)
    window.addEventListener('slamSettingsChanged', handleSettingsChange);

    // Also sync on mount in case sessionStorage was updated while component wasn't mounted
    const schema = sportToPortalSchema(getSelectedSport());
    if (schema !== selectedSchema) {
      setSelectedSchema(schema);
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('slamSettingsChanged', handleSettingsChange);
    };
  }, [selectedSchema]);

  // Function to refresh Roster data with correct data source based on human-in-loop setting
  const refreshPlayerData = () => {
    if (humanInLoop) {
      // When human-in-loop is enabled, use the overridden data
      setMatchedPlayers(getRecommendedPlayersWithOverrides());
      setRejectedPlayers(getRejectedPlayersWithOverrides());
    } else {
      // When disabled, use the pure AI recommendations
      setMatchedPlayers(getRecommendedPlayers());
      setRejectedPlayers(getRejectedPlayers());
    }

    setAgentDescriptions(getAgentDescriptions());
    setEvaluationHistory(getEvaluationHistory());
  };
  const [wbb_data] = useState(wbb_nil);

  // Fetch transfer portal players
  const fetchTransferPortalPlayers = async (
    schema = "CBB",
    page = 1,
    size = 5
  ) => {
    if (!searchTerm?.length) {
      setTransferPortalLoading(true);
    }
    try {
      if (schema === "WBB") {
        // For Women's Basketball, use the local JSON file
        try {
          const data = wbb_data;
          // Transform the data to match the expected format
          const transformedData = data.map((player) => ({
            player_id: player.profile_url.split("/").pop().replace("/", ""), // Extract ID from URL
            player_name: player.name,
            position: player.position,
            class: player.year,
            latest_team: player.last_team,
            destination_team: player.new_team || "--",
            date_entered: player.date_entered || new Date().toISOString(),
            image_url: player.image_url,
            SLAM_SCORE: Math.floor(Math.random() * 40) + 60, // Generate a random score between 60-100
            status: player.status,
            nil_value: player.nil_value,
          }));

          setTransferPortalPlayers(transformedData);
        } catch (error) {
          console.error("Error fetching Women's Basketball NIL data:", error);
        }
      } else if (schema === "MBB") {
        // For Women's Basketball, use the local JSON file
        try {
          const skip = (page - 1) * size;
          const buildQueryParams = () => {
            // Start with required parameters
            const params = new URLSearchParams({
              page: page,
              page_size: size,
              schema: schema,
              sort_by: sortField,
              sort_order: sortDirection,
            });

            // Only add filter parameters if they have values
            if (filters?.position?.length) {
              params.append("position", filters?.position);
            }
            if (searchTerm?.length) {
              params.append("name", searchTerm);
            }
            if (filters?.class?.length) {
              params.append("class", filters?.class);
            }
            if (filters?.latestTeam?.length) {
              // const encodedTeam = encodeURIComponent(filters.latestTeam);
              params.append("team", filters.latestTeam);
            }
            if (filters?.min_possessions) {
              params.append("min_possessions", filters?.min_possessions);
            }

            return params.toString();
          };

          const queryString = buildQueryParams();
          const response = await apiClient.get(
            `https://slam-all-python-359065791766.us-central1.run.app/MBB/tp-players/allplayer?${queryString}`
          );
          const transformedData = response?.data?.data?.map((player) => ({
            player_id: player.player_id, // Extract ID from URL
            player_name: player.name || "N/A",
            position: player.position || "N/A",
            class: player.class || "N/A",
            latest_team: player.team || "N/A",
            destination_team: player.new_team || "N/A",
            date_entered: player.date_entered,
            image_url: player.image_url,
            advance_stats: player?.stats || {},
            core_stats: player?.core_stats || {}
          }));
          setTotalPlayers(response.data?.total || 0);
          setTransferPortalPlayers(transformedData);
        } catch (error) {
          console.error("Error fetching Women's Basketball NIL data:", error);
        }
      } else {
        const skip = (page - 1) * size;
        const buildQueryParams = () => {
          // Start with required parameters
          const params = new URLSearchParams({
            skip: skip,
            limit: size,
            schema: schema,
            sort_by: sortField,
            min_score: filters?.slamScore?.[0],
            max_score: filters?.slamScore?.[1],
            sort_order: sortDirection,
          });

          // Only add filter parameters if they have values
          if (filters?.position?.length) {
            params.append("position", filters?.position);
          }
          if (searchTerm?.length) {
            params.append("name", searchTerm);
          }
          if (filters?.class?.length) {
            params.append("_class", filters?.class);
          }
          if (filters?.latestTeam?.length) {
            // const encodedTeam = encodeURIComponent(filters.latestTeam);
            params.append("team", filters.latestTeam);
          }
          if (filters?.destination?.length) {
            params.append("destination_team", filters?.destination);
          }
          if (filters?.min_possessions) {
            params.append("min_possessions", filters?.min_possessions);
          }

          return params.toString();
        };

        const queryString = buildQueryParams();
        const response = await apiClient.get(
          `https://slam-all-python-359065791766.us-central1.run.app/CBB/transfer-portal/players?${queryString}`
        );

        // const response = await apiClient.get(`https://slam-all-python-359065791766.us-central1.run.app/CBB/transfer-portal/players?skip=${skip}&limit=${size}&schema=${schema}&sort_by=${sortField}&sort_order=${sortDirection}&position=${filters?.position}&_class=${filters?.class}&team=${filters?.latestTeam}&destination_team=${filters?.destination}`);
        setTransferPortalPlayers(response.data?.data);
        setTotalPlayers(response.data?.total || 0);
      }
    } catch (error) {
      console.error("Error fetching transfer portal players:", error);
    } finally {
      setTransferPortalLoading(false);
    }
  };

  // this will generate a csv file
  const handleExport = () => {
    if (!transferPortalPlayers || transferPortalPlayers.length === 0) {
      alert("No player data available to export");
      return;
    }

    try {
      // CSV Headers
      const headers = [
        "Player Name",
        "Position",
        "Class",
        "Latest Team",
        "Destination",
        "Date Entered",
        "Slam Score",
        "Status",
      ];

      // Convert player data to CSV format
      const csvRows = transferPortalPlayers.map((player) => [
        `"${player.player_name || "-"}"`,
        `"${player.position || "-"}"`,
        `"${player.class || "-"}"`,
        `"${player.latest_team || "-"}"`,
        `"${player.destination_team || "--"}"`,
        `"${player.date_entered
          ? new Date(player.date_entered).toISOString().split("T")[0]
          : "-"
        }"`,
        `"${player.SLAM_SCORE || "-"}"`,
        `"${player.status || player.nil_value || "unlocked"}"`,
      ]);

      // Combine headers and data
      const csvContent = [headers, ...csvRows]
        .map((row) => row.join(","))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Generate filename with current date and schema
      const currentDate = new Date().toISOString().split("T")[0];
      const schemaName =
        selectedSchema === "CBB"
          ? "college_baseball"
          : selectedSchema === "WBB"
            ? "womens_basketball"
            : selectedSchema === "MBB"
              ? "mens_basketball"
              : "transfer_portal";

      link.download = `${schemaName}_players_${currentDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`Exported ${transferPortalPlayers.length} players to CSV`);
    } catch (error) {
      console.error("Error exporting CSV:", error);
      alert("Error occurred while exporting data");
    }
  };

  // Handle schema change
  const handleSchemaChange = (schema) => {
    setSelectedSchema(schema);
    setCurrentPage(1); // Reset to first page when schema changes
    fetchTransferPortalPlayers(schema, 1, pageSize);
  };

  // Handle page change
  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
    fetchTransferPortalPlayers(selectedSchema, page, size);
  };

  // Handle decision override
  const handleOverrideDecision = () => {
    if (!selectedPlayer || overrideDecision === null) return;

    try {
      const success = overridePlayerDecision(
        selectedPlayer.Roster.id,
        overrideDecision
      );

      if (success) {
        // Show success feedback
        setOverrideFeedback({
          show: true,
          message: `Roster ${selectedPlayer.Roster.first_name} ${selectedPlayer.Roster.last_name
            }'s evaluation was successfully ${overrideDecision ? "approved" : "rejected"
            } by human override.`,
          isError: false,
        });

        // Refresh the data
        refreshPlayerData();

        // Clear the override state and close modal
        setOverrideReason("");
        setOverrideModalOpen(false);

        // Auto-hide feedback after 5 seconds
        setTimeout(() => {
          setOverrideFeedback({ show: false, message: "", isError: false });
        }, 5000);
      } else {
        // Show error feedback
        setOverrideFeedback({
          show: true,
          message: "Unable to override decision. Please try again.",
          isError: true,
        });
      }
    } catch (error) {
      console.error("Error overriding decision:", error);
      setOverrideFeedback({
        show: true,
        message: "An error occurred while overriding the decision.",
        isError: true,
      });
    }
  };

  // Toggle human-in-loop mode
  const toggleHumanInLoop = () => {
    setHumanInLoop((prev) => !prev);
  };

  // Use the TransferPortalTab's method to update saved evaluations
  const handleSavedEvaluations = (playerId, data) => {
    if (typeof setSavedEvaluations === "function") {
      setSavedEvaluations((prev) => ({
        ...prev,
        [playerId]: data,
      }));
    }
  };

  // Check for existing saved evaluation
  const checkSavedEvaluation = async (playerId) => {
    try {
      const response = await apiClient.get(
        `https://slam-all-python-359065791766.us-central1.run.app/${selectedSchema}/transfer-portal/tp-evaluation/${playerId}?schema=${selectedSchema}`
      );
      if (response.data) {
        // Now we have the state setter properly defined in the component
        setSavedEvaluations((prev) => ({
          ...prev,
          [playerId]: response.data,
        }));
        return true;
      }
    } catch (error) {
      // 404 is expected for players without saved evaluations — suppress silently
      if (error?.response?.status !== 404) {
        console.error(
          `Error checking saved evaluation for player ${playerId}:`,
          error
        );
      }
    }
    return false;
  };

  // Load saved evaluations for all players on page
  const loadSavedEvaluations = async () => {
    // We don't have a bulk endpoint, so we'll fetch evaluations for each player
    // This is not ideal for performance, but works for this demo
    const playerPromises = transferPortalPlayers?.map((player) =>
      checkSavedEvaluation(player.player_id).catch((err) => false)
    );

    await Promise.all(playerPromises);
  };

  useEffect(() => {
    // Refresh data whenever human-in-loop setting changes
    if (!loading) {
      refreshPlayerData();
    }
  }, [humanInLoop]);

  // Load saved evaluations when players change
  useEffect(() => {
    if (transferPortalPlayers?.length > 0) {
      loadSavedEvaluations();
    }
  }, [transferPortalPlayers]);

  useEffect(() => {
    // Simulate loading time to show the matching engine "working"
    if (!searchTerm?.length) {
      setLoading(true);
    }
    const timer = setTimeout(() => {
      refreshPlayerData();
      setLoading(false);
    }, 1500);

    // Fetch transfer portal players
    fetchTransferPortalPlayers(selectedSchema, currentPage, pageSize);

    return () => clearTimeout(timer);
  }, [sortField, sortDirection, filters, searchTerm, selectedSchema]);

  return (
    <div
      className="page-container"
      style={{
        background: C.s1,
        color: C.text,
        minHeight: '100%',
      }}
    >
      {/* Ambient glow */}
      <div
        className="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-20 blur-3xl pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${C.gold}4D 0%, transparent 70%)`,
        }}
      />

      <div className="relative p-6">
        {/* Header */}
        <div
          className="rounded-2xl p-6 mb-6"
          style={{
            background: C.s1,
            border: `1px solid ${C.border}`,
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 style={{ fontSize: 24, fontWeight: 200, letterSpacing: -0.5, color: C.gold, marginBottom: 4 }}>
                Transfer Portal
              </h2>
              <p style={{ color: C.t3, fontSize: 13 }}>
                {selectedSchema === 'CBB'
                  ? 'Baseball transfer portal — manage and track your prospects'
                  : 'Basketball transfer portal — manage and track your prospects'}
              </p>
            </div>

            <div className="flex items-center justify-end space-x-3">
              {/* Upload List — basketball only (MBB GCP endpoint exists) */}
              {selectedSchema !== 'CBB' && (
                <button
                  className="flex items-center gap-2 text-sm font-medium px-4 py-2.5 rounded-xl transition-all duration-300"
                  style={{
                    background: isOpenPlayerModal && modalType === "listForm" ? C.gold : C.s2,
                    border: `1px solid ${isOpenPlayerModal && modalType === "listForm" ? C.goldDim : C.border}`,
                    color: isOpenPlayerModal && modalType === "listForm" ? '#000' : C.text,
                  }}
                  onClick={() => {
                    setIsOpenPlayerModal(true);
                    setModalType("listForm");
                  }}
                  onMouseEnter={(e) => {
                    if (!(isOpenPlayerModal && modalType === "listForm")) {
                      e.currentTarget.style.borderColor = C.goldDim;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!(isOpenPlayerModal && modalType === "listForm")) {
                      e.currentTarget.style.borderColor = C.border;
                    }
                  }}
                >
                  <Upload className="w-4 h-4" />
                  Upload List
                </button>
              )}

              {/* Export — available for all sports (local CSV generation) */}
              <button
                className="flex items-center gap-2 text-sm font-medium px-4 py-2.5 rounded-xl transition-all duration-300"
                style={{
                  background: C.s2,
                  border: `1px solid ${C.border}`,
                  color: C.text,
                  opacity: transferPortalLoading || transferPortalPlayers.length === 0 ? 0.5 : 1,
                  cursor: transferPortalLoading || transferPortalPlayers.length === 0 ? 'not-allowed' : 'pointer',
                }}
                onClick={handleExport}
                disabled={transferPortalLoading || transferPortalPlayers.length === 0}
                title={
                  transferPortalLoading
                    ? "Loading data..."
                    : transferPortalPlayers.length === 0
                      ? "No data available to export"
                      : "Export player data as CSV"
                }
                onMouseEnter={(e) => {
                  if (!(transferPortalLoading || transferPortalPlayers.length === 0)) {
                    e.currentTarget.style.borderColor = C.goldDim;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = C.border;
                }}
              >
                <Download className="w-4 h-4" />
                {transferPortalLoading ? "Loading..." : "Export"}
              </button>

              {/* Add New Recruit — basketball only (MBB GCP endpoint exists) */}
              {selectedSchema !== 'CBB' && (
                <button
                  className="flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl transition-all duration-300"
                  style={{
                    background: C.gold,
                    border: `1px solid ${C.goldDim}`,
                    boxShadow: `0 4px 20px ${C.gold}4D`,
                    color: '#000',
                  }}
                  onClick={() => {
                    setIsOpenPlayerModal(true);
                    setModalType("inputForm");
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = `0 6px 24px ${C.gold}66`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = `0 4px 20px ${C.gold}4D`;
                  }}
                >
                  <Plus className="w-4 h-4" />
                  Add New Recruit
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Feedback Alert */}
        {overrideFeedback.show && (
          <div
            className={`mb-4 p-3 rounded-[10px] ${overrideFeedback.isError
              ? "bg-red-900/30 text-red-400"
              : "bg-green-900/30 text-green-400"
              }`}
          >
            <div className="flex items-center">
              {overrideFeedback.isError ? (
                <AlertTriangle
                  size={18}
                  className="mr-2 text-red-400"
                />
              ) : (
                <CheckCircle
                  size={18}
                  className="mr-2 text-green-400"
                />
              )}
              <p>{overrideFeedback.message}</p>
            </div>
          </div>
        )}

        {/* View Selector - Only Transfer Portal Data tab remains */}
        {/* <div
          className={`flex ${isDarkMode ? "border-gray-700" : "border-b"} mb-6`}
        >
          <button
            className={`px-6 py-2 font-medium flex items-center text-yellow-600 ${isDarkMode ? "border-yellow-500" : "border-yellow-600"
              } border-b-2`}
          >
            <Filter size={18} className="mr-2" />
            Transfer Portal Data
          </button>
        </div> */}

        {loading ? (
          <LoadingState />
        ) : (
          <>
            <EvaluationHistory
              setSearchTerm={setSearchTerm}
              searchTerm={searchTerm}
              totalPlayers={totalPlayers}
              transferPortalPlayers={transferPortalPlayers}
              transferPortalLoading={transferPortalLoading}
              selectedSchema={selectedSchema}
              setSortField={setSortField}
              setSortDirection={setSortDirection}
              setFilters={setFilters}
              filters={filters}
              sortDirection={sortDirection}
              sortField={sortField}
              onSchemaChange={handleSchemaChange}
              savedEvaluations={savedEvaluations}
              setSavedEvaluations={setSavedEvaluations}
            />
            {/* Ant Design Pagination */}
            {selectedSchema != "WBB" ? (
              <div className="flex justify-center mt-6">
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={totalPlayers}
                  onChange={handlePageChange}
                  showSizeChanger
                  pageSizeOptions={["5", "10", "20", "50"]}
                  isDarkMode={true}
                />
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* Override Decision Modal */}
      <OverrideModal
        isOpen={overrideModalOpen}
        onClose={() => setOverrideModalOpen(false)}
        Roster={selectedPlayer?.Roster}
        currentDecision={selectedPlayer?.finalDecision.match}
        onOverride={handleOverrideDecision}
        overrideDecision={overrideDecision}
        setOverrideDecision={setOverrideDecision}
        overrideReason={overrideReason}
        setOverrideReason={setOverrideReason}
      />
      {/* this modal is for new recruiter */}
      <PlayerModal
        isOpenPlayerModal={isOpenPlayerModal}
        setIsOpenPlayerModal={setIsOpenPlayerModal}
        modalType={modalType}
        players={dropdownPlayers}
      />
    </div>
  );
};
export default MatchingEngine;
